import React, { useState, useRef, useEffect, useCallback, useReducer } from 'react';
import { useNavigate } from "react-router-dom";
import { ArrowDropDown } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  IconButton,
  MenuItem,
  Button,
  Select,
  Autocomplete,
  TextField,  
  InputAdornment,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ClearIcon from '@mui/icons-material/Clear';
import { DeleteForeverRounded } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AlertNotification from '../../components/AlertNotification';
import ToggleSplitPane from '../../components/ToggleSplitPane';
import CaseInformationPane from '../../components/Workflow/CaseInformation';
import PageLoader from '../../components/PageLoader';
import {
  CaseWorkflowService,
  TextAreaWithCount,
  TextFieldWithIconButton,
  CustomAutoComplete,
  UtilityLibrary,
  ApiManager,
  DebounceForm,
  ValidateRequiredFieldsInForm,
  ValidateForm,
  sessionService,
  SessionManager,
  ConfirmationDialog,
  NotificationDialog,
  EnumUtility,
  ObjectUtility,
  batchStatusModel,
  NotificationDialogModel,
  CaseDocumentsModel, 
  NarrativeModel,
  DeterminePermissions,
  LoggerUtility,
  TimerUtility,
  SharedService
} from '../../components/ComponentsLibrary';

import { ClientType } from '../../enums/ClientType'
import { useConfigContext } from '../../functions/ConfigContext';
import { useNavigationContext } from '../../functions/NavigationContext';

// temporary holiday list until we get a holiday api state specific call made
const holidays = [
  { date: "01-01", name: "New Year's Day" },
  { date: "01-15", name: "Martin Luther King Jr. Day" },
  { date: "02-19", name: "Presidents' Day" },
  { date: "05-27", name: "Memorial Day" },
  { date: "06-19", name: "Juneteenth" },
  { date: "07-04", name: "Independence Day" },
  { date: "09-02", name: "Labor Day" },
  { date: "10-14", name: "Columbus Day" },
  { date: "11-11", name: "Veterans Day" },
  { date: "11-28", name: "Thanksgiving Day" },
  { date: "12-25", name: "Christmas Day" }
];

const getToday = () => new Date();
const getNYDateToday = () => {const today = getToday();
return new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())}



const FormFieldEnableType = {
  PageLoad: { ID: 100, name: "PageLoad" },
  NewWorkItemClick: { ID: 200, name: "NewWorkItemClick" },
  CaseNumberValid: { ID: 300, name: "CaseNumberValid" },
  CaseNumberInValid: { ID: 350, name: "CaseNumberInValid" },
  NoCaseSelect: { ID: 400, name: "NoCaseSelect" },
  WorkflowSelect: { ID: 500, name: "WorkflowSelect" },
  ActionTypeSelect: { ID: 501, name: "ActionTypeSelect" },
  ActionTypeSelectWithICC: { ID: 502, name: "ActionTypeSelectWithICC" },
  IccSelect: { ID: 503, name: "IccSelect" },
  SaveNewDoc: { ID: 600, name: "SaveNewDoc" },
  SaveAnother: { ID: 700, name: "SaveAnother" },
  PreviousCaseSelect: { ID: 800, name: "PreviousCaseSelect" },
  PreviousCaseDeSelect: { ID: 900, name: "PreviousCaseDeSelect" },
  RemoveFromActionList: { ID: 1000, name: "RemoveFromActionList" } 
};

const FormActionType = {
  Save: { ID: 100, name: "Save" },
};

const CaseSearchSource = {
  CaseNumberField: { ID: 100, name: "CaseNumberField" },
  PreviousCaseField: { ID: 200, name: "PreviousCaseField" },
};

// Initial state for form fields
const initialState = {
  caseNumberTxt: false,
  noCaseChk: false,
  workflowSelect: false,
  iccSelect: false,
  actionTypeSelect: false,
  nextActionTypeSelect: false,
  nextActionDueDateTxt: false,
  addToListBtn: false,
  noteForActionTxt: false,
  narrativeForStateTxt: false,
  saveBtn: false,
  iccViewEnabled: false,
};

// Reducer to manage enable/disable state
function reducer(state, action) {
  switch (action.type) {
    case "SET_FIELD_STATE":
      return { ...state, [action.payload.field]: action.payload.isEnabled };
    case 'SET_BUTTON_VISIBILITY':
      return { ...state, [action.payload.field]: action.payload.isVisible };
    case "SET_STATE_VARIABLE":
      return {
        ...state,
        hasBatchLocked: action.payload,
      };
    case "SET_FIELD_VISIBILITY":
      return {
        ...state,
        [`${action.payload.field}Visible`]: action.payload.isVisible
      };
    default:
      throw new Error("Unknown action type");
  }
};

function checkNextActionEnabled(config) {
  if (config) {
    if (config.isNextActionEditEnabled) {
      return true;
    }
    else {
      return false;
    }
  }
  else {
    return false;
  }
};

// Function to apply Form Fields Enable logic
function formFieldsEnableLogic(dispatch, logicType, config = {}) {

  switch (logicType) {
    case FormFieldEnableType.PageLoad.ID:
    case FormFieldEnableType.NewWorkItemClick.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noCaseChk", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "workflowSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_BUTTON_VISIBILITY", payload: { field: "skipDocumentBtn", isVisible: false } });
      dispatch({ type: "SET_FIELD_VISIBILITY", payload: { field: "iccSelect", isVisible: false } });
      break;
      
    case FormFieldEnableType.CaseNumberValid.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "workflowSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });

      dispatch({ type: "SET_FIELD_VISIBILITY", payload: { field: "iccSelect", isVisible: false } });
      break;

    case FormFieldEnableType.CaseNumberInValid.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "workflowSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      
      dispatch({ type: "SET_FIELD_VISIBILITY", payload: { field: "iccSelect", isVisible: false } });
      break;

    case FormFieldEnableType.NoCaseSelect.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "workflowSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_BUTTON_VISIBILITY", payload: { field: "skipDocumentBtn", isVisible: false } });
      break;

    case FormFieldEnableType.WorkflowSelect.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "workflowSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTypeSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: false } });
      // dispatch({ type: "SET_FIELD_VISIBILITY", payload: { field: "iccSelect", isVisible: formValues.workflowSelectedRow?.enableAddTabInputChannelChoice || false} });
      break;

    case FormFieldEnableType.ActionTypeSelect.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTypeSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      if (checkNextActionEnabled(config)) {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: true } });
      }
      else {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: true } });
        // Don't disable date field - let it be controlled by handleNextActionTypeChange
      }
      break;

    case FormFieldEnableType.ActionTypeSelectWithICC.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTypeSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_VISIBILITY", payload: { field: "iccSelect", isVisible: true } });
      if (checkNextActionEnabled(config)) {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: true } });
      }
      else {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: true } });
        // Don't disable date field - let it be controlled by handleNextActionTypeChange
      }
      break;

    case FormFieldEnableType.IccSelect.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: true } });
      if (checkNextActionEnabled(config)) {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: true } });
      }
      else {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: true } });
        // Don't disable date field - let it be controlled by handleNextActionTypeChange
      }
      break;

    case FormFieldEnableType.RemoveFromActionList.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      // dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isVisible: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: false } });
      break;
    default:
      break;
  }
};



// Helper function to update form values
function updateArrayStateValues(arrayStateSetter, updatedValues) {
  arrayStateSetter((prevValues) => ({
    ...prevValues,
    ...updatedValues
  }));
};



function getParentIdByChildId(data, parentFieldName, childField, childId) {
  try {
    const parent = data.find((parent) =>
      parent[childField] === childId)
    ;
    return parent ? parent[parentFieldName] : 0; // Return 0 if no parent is found
  }
  catch (error) {
    throw LoggerUtility.ProcessErrorData(error, "getParentIdByChildId", {});
  }
};

function getParentIdByChildId2(data, parentFieldName, childFieldName, childField, childId) {
  try {
    const parent = data.find((parent) =>
      parent[childFieldName].some((child) => child[childField] === childId)
    );
    return parent ? parent[parentFieldName] : 0; // Return 0 if no parent is found
  }
  catch (error) {
    throw LoggerUtility.ProcessErrorData(error, "getParentIdByChildId", {});
  }
};


const NewWorkitem = React.memo(({ }) => {
  function isUserAllowedNextActionDue() {
  const loginUserInfo = sessionService.getUser();
  const allowedRoleIDs = [1, 2, 3]; // IT Admin, Supervisor, Lead
  const userRoleID =
    loginUserInfo && typeof loginUserInfo.roleID === "number"
      ? loginUserInfo.roleID
      : -1;

  return allowedRoleIDs.includes(userRoleID);
}

function handleNextActionDueDateChange(selectedDate) {
  if (!selectedDate || isNaN(selectedDate.getTime())) {
    return;
  }

  // Build YYYY-MM-DD in LOCAL time (no UTC conversion)
  const year = selectedDate.getFullYear();
  const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
  const day = String(selectedDate.getDate()).padStart(2, "0");
  const formattedDate = `${year}-${month}-${day}`;

  setFormValues((prev) => {
    if (prev.nextActionDueDateTxt === formattedDate) {
      return prev;
    }

    // Validate past date
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selected = new Date(year, Number(month) - 1, day);
    if (selected < today) {
      showAlert("error", "You cannot select a past date.");
      return prev;
    }

    // Validate weekend
    const dayOfWeek = selected.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      showAlert("error", "You cannot select a weekend date.");
      return prev;
    }
    return {
      ...prev,
      nextActionDueDateTxt: formattedDate,
    };
  });
}

function handleNextActionDueDateChangeClear() {
  setFormValues((prev) => ({ ...prev, nextActionDueDateTxt: null }));
}

useEffect(() => {
  document.title = "YTrac :: New Work Item";
}, []);

useEffect(() => {
  // Initialize asterisks on component mount
  const initialAsterisks = calculateRequiredAsterisks(formValues);
  setRequiredFieldLabels(initialAsterisks);
}, []);


  const navigate = useNavigate();
  const { setActivePage } = useNavigationContext();

  const leftPaneTitle = "New Work Item";
  const rightPaneTitle = "Case Information";

  const errorFormName = "New Work Item";
  const { configLibrary } = useConfigContext();
  // Create a ref for the child component
  const caseInfoRef = useRef(null);

  const masterServerDBSchema = SessionManager.GetDataFromSession("SiteSettingsDetailsKey")?.masterDBSchema;

  const currentDate = UtilityLibrary.formatDate(new Date().toISOString(), "YYYY-MM-DD");

  const siteSettingDetail = JSON.parse(SessionManager.GetValueFromSession(SessionManager.SiteSettingsDetailsKey));

  const initWorkflow = { id: -123456, name: "" };
  const initActionTypeSelect = { id: -123456, name: "" };
  const initNextActionTypeSelect = { id: -123456, name: "" };
  const initICCSelect = { id: -123456, name: "" };

  const initWorkflowSelectedRow = { 'workflowID': -123456, 'workflowName': "", 'enableAddTabNoCase': false, 'enableAddTabInputChannelChoice': false, 'enableAddTabOverrideStepDate': false, 'fkM_NoteTemplateID': 0, 'departmentName': "", };
  const initActionTypeSelectedItem = { actionTypeID: -123456, workflowID: 0, actionType: "", displayOrder: 0, initialWorkflowStepID: 0, workflowStartID: 0, workflowStartTypeID: 0, workflowMapID: 0 };
  const initNextActionTypeSelectedItem = { nextActionTypeID: -123456, workflowID: 0, workflowStepID: 0, workflowStepName: "", workflowMapID: 0, displayOrder: 0, departmentName: "", };
  
  const initialFormValues = {
    caseNumberTxt: "",
    yTracCaseID: 0,
    caseNumberSearched: "",
    noCaseChk: false,
    isNoCase: 0,
    workflowSelect: initWorkflow,
    workflowSelectedRow: initWorkflowSelectedRow,
    iccSelect: initICCSelect,
    iccViewEnabled: false,
    actionTypeSelect: initActionTypeSelect,
    actionTypeSelectedItem: initActionTypeSelectedItem,
    nextActionTypeSelect: initNextActionTypeSelect,
    nextActionTypeSelectedItem: initNextActionTypeSelectedItem,
    nextActionDueDateTxt: null,
    noteForActionTxt: "",
    narrativeForStateTxt: "",
    workflowStartID: 0,
  };

  const clearSaveFormValues = {
    caseNumberTxt: "",
    yTracCaseID: 0,
    caseNumberSearched: "",
    noCaseChk: false,
    isNoCase: 0,
    workflowSelect: initWorkflow,
    workflowSelectedRow: initWorkflowSelectedRow,
    iccSelect: initICCSelect,
    iccViewEnabled: false,
    actionTypeSelect: initActionTypeSelect,
    actionTypeSelectedItem: initActionTypeSelectedItem,
    nextActionTypeSelect: initNextActionTypeSelect,
    nextActionTypeSelectedItem: initNextActionTypeSelectedItem,
    nextActionDueDateTxt: null,
    noteForActionTxt: "",
    narrativeForStateTxt: "",
    workflowStartID: 0,
  };


  const [requiredFieldLabels, setRequiredFieldLabels] = useState({
    caseNumberTxt: "",
    noCaseChk: "",
    workflowSelect: "",
    iccSelect: "",
    actionTypeSelect: "",
    nextActionTypeSelect: "",
    nextActionDueDateTxt: "",
    noteForActionTxt: "",
    narrativeForStateTxt: ""
  });

  const [formState, dispatch] = useReducer(reducer, initialState);
  const [formValues, setFormValues] = useState(initialFormValues);

  const initialCaseNumberIconsDisabled = { search: false, copy: false, clear: false };
  const [caseNumberIconsDisabled, setCaseNumberIconsDisabled] = useState(initialCaseNumberIconsDisabled);

  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  const [caseSaveAnotherArray, setCaseSaveAnotherArray] = useState([]);
  const [caseValidationType, setCaseValidationType] = useState(EnumUtility.CaseType.Case);

  const [paginationModel, setPaginationModel] = useState({ pageSize: 5, page: 0 });
  const [initialSearch, setInitialSearch] = useState(false);
  const [gridFilterModel, setGridFilterModel] = useState({ items: [] });
  const [hasListOfActions, setHasListOfActions] = useState(true);

  const [workflowList, setWorkflowList] = useState([]);
  const [workflowOptions, setWorkflowOptions] = useState([]);
  const [workflowCaseOptions, setWorkflowCaseOptions] = useState([]);
  const [workflowNoCaseOptions, setWorkflowNoCaseOptions] = useState([]);

  const [iccListOptions, setIccListOptions] = useState([]);
  const [actionTypeOptions, setActionTypeOptions] = useState([]);
  const [nextActionTypeOptions, setNextActionTypeOptions] = useState([]);
  const [isNextActionEditable, setIsNextActionEditable] = useState(false);

  const [disabledNextAction, setDisableNextAction] = useState(false);


  let listOfActionsGridIndex = 0;
  const [resultsListOfActions, setResultsListOfActions] = useState([]);
  const [removeListOfAction, setRemoveListOfAction] = useState(null);
  const [holidayList, setHolidayList] = useState([])
  const [areDropdownsLocked, setAreDropdownsLocked] = useState(false);

  const listOfActionsGridRows = [
    { id: 1, action: 'Escalated', workflow: 'Documents', queue: 'Doc-M (Mod) - Level 2', daystoNext: '0', nextActionType: 'Work Doc-M', },
  ];

  //const [resultsListOfActions, setListOfActionsResults] = useState(listOfActionsGridRows);

  // Data for the document grid in view 2
  const listOfActionsGridColumns = [
    {
      field: 'action', headerName: 'Action', flex: 1,
      renderCell: (params) => {
        return (
          <div className="d-flex justify-content-start align-items-center" style={{ cursor: "pointer" }}>
            <DeleteForeverRounded index={params.row.id}
              onClick={() => handleRemovActionConfirmationDialog(params.row.id)}
              color='ywMainBlue' />
            <div style={{ paddingLeft: "10px" }}>
              {params.row.action}
            </div>
          </div>
        );
      }
    },
    { field: 'workflow', headerName: 'Workflow', flex: 1 },
    { field: 'queue', headerName: 'Queue', flex: 1 },
    { field: 'daystoNext', headerName: 'Days to Next', flex: 1 },
    { field: 'nextActionType', headerName: 'Next Action Type', flex: 1 },
  ];


  function clearFormFields(formActionType) {
    try {
      let validationType = EnumUtility.CaseType.Case;

      switch (formActionType) {
        case FormActionType.Save.ID:
          const updatedFormValues = { ...formValues, ...clearSaveFormValues };
          updateArrayStateValues(setFormValues, updatedFormValues);
          noteForActionRef.current?.clear();
          narrativeForStateRef.current?.clear();
          setCaseValidationType(validationType);
          debouncedValidation(updatedFormValues, validationType);
          break;

        default:
          setCaseValidationType(validationType);
          debouncedValidation(initialFormValues, validationType);
          break;
      }
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearFormFields", {});
    }

  };

  function getValidationRules(values, validationType) {
    switch (validationType) {
      case EnumUtility.CaseType.Case:
        return getCaseValidationRules(values);

      case EnumUtility.CaseType.NoCase:
        return getNoCaseValidationRules(values);

      default:
        break;
    }
  };

  const getCaseValidationRules = (values) => {

    let caseNumberValidation = { required: false };  
    if(siteSettingDetail.CaseNumberInsertAllow === "1") {
      caseNumberValidation = { required: true, validator: () => values.caseNumberSearched !== "", requireValueChange: true, };
    } 
    else {
      caseNumberValidation = { required: true, validator: () => values.yTracCaseID > 0, requireValueChange: true, };
    }

    return {
      caseNumberTxt: caseNumberValidation,
      noCaseChk: { required: values.caseNumberTxt === '' ? true : false },
      noteForActionTxt: { required: false },
      narrativeForStateTxt: { required: true, validator: (value) => value !== "" }
    };
  };

  const getNoCaseValidationRules = (values) => {
    return {
      batchSelect: { required: true, validator: (value) => value !== "" },
      caseNumberTxt: { required: false },
      noCaseChk: { required: true, validator: (value) => value === true },
      noteForActionTxt: { required: true, validator: (value) => value !== "" },
      narrativeForStateTxt: { required: false }
    };
  };

  const getDefaultValidationRules = (values) => ({
    batchSelect: { required: true, validator: (value) => value !== "" },
    caseNumberTxt: { required: true, validator: () => values.yTracCaseID > 0 },
    noCaseChk: { required: true, validator: (value) => value === true },
    noteForActionTxt: { required: true, validator: (value) => value !== "" },
    narrativeForStateTxt: { required: true, validator: (value) => value !== "" }
  });

  // Custom function to calculate which fields should show asterisks
  const calculateRequiredAsterisks = (values) => {
    const asterisks = {
      caseNumberTxt: "",
      noCaseChk: "",
      workflowSelect: "",
      actionTypeSelect: "",
      iccSelect: "",
      nextActionTypeSelect: "",
      nextActionDueDateTxt: "",
      noteForActionTxt: "",
      narrativeForStateTxt: ""
    };


    const hasCaseSearched = values.caseNumberSearched && values.caseNumberSearched.trim() !== "";
    const hasNoCaseSelected = values.noCaseChk;
    const shouldShowAsterisks = hasCaseSearched || hasNoCaseSelected;

    if (!shouldShowAsterisks) {
      return asterisks;
    }


    asterisks.workflowSelect = "";


    const isActionTypeSelected = values.actionTypeSelect && values.actionTypeSelect.id > 0;
    const isWorkflowSelected = values.workflowSelect && values.workflowSelect.id > 0;
    if (isWorkflowSelected) {
      asterisks.actionTypeSelect = isActionTypeSelected ? "" : "*";
    }

    const isNextActionTypeSelected = values.nextActionTypeSelect && values.nextActionTypeSelect.id > 0;
    if (isActionTypeSelected) {
      asterisks.nextActionTypeSelect = isNextActionTypeSelected ? "" : "*";
    }

    if (values.noCaseChk) {
      // No Case mode: Note is required, Narrative is not required
      const isNoteFilledOut = values.noteForActionTxt && values.noteForActionTxt.trim() !== "";
      asterisks.noteForActionTxt = isNoteFilledOut ? "" : "*";
      asterisks.narrativeForStateTxt = ""; // Not required in No Case mode
    } else {
      // Case Search mode: Narrative is required, Note is optional
      const isNarrativeFilledOut = values.narrativeForStateTxt && values.narrativeForStateTxt.trim() !== "";
      asterisks.narrativeForStateTxt = isNarrativeFilledOut ? "" : "*";
      asterisks.noteForActionTxt = ""; // Not required in Case Search mode
    }

    return asterisks;
  };

  // Debounced validation using the centralized validateForm
  const debouncedValidation = useCallback(
    DebounceForm((values, validationType) => {
      try {
        const rules = getValidationRules(values, validationType);

        const validationResults = ValidateRequiredFieldsInForm(values, rules, formState);

        // Use custom asterisk calculation for proper No Case handling
        const customAsterisks = calculateRequiredAsterisks(values);
        setRequiredFieldLabels(customAsterisks);
        
        const isValid = validationResults.isFormValid;

        setIsSaveEnabled(isValid);

      }
      catch (error) {
        throw LoggerUtility.ProcessErrorData(error, "debouncedValidation", {});
      }
    }, 300),
    []
  );

  const [isEditable, setIsEditable] = useState(false);

  //Batch dropdown API code 
  const [batchlists, setBatchlists] = useState([]);

  const [externalCaseID, setExtCaseID] = useState(0);
  const [extCaseSearch, setExtCaseSearch] = useState(false);
  const [showExtDocGrid, setShowExtDocGrid] = useState(false);

  const caseNumberRef = useRef();
  const caseUniqueID = "caseNumber";

  const caseNumberRegex = siteSettingDetail.CaseNumberRegex || { caseRegexValue: "", caseRegexMessage: "" };
  // Narrative Character Limit
  const narrativeCharLimit = SessionManager.GetValueFromSession(SessionManager.NarrativeCharsLimitKey) || 500;

  // Notes Character Limit
  const notesCharLimit = SessionManager.GetValueFromSession(SessionManager.NotesCharsLimitKey) || 500;

  const [lockedBatchInfo, setLockedBatchInfo] = useState({
    hasLockedBatch: false,
    lockedBatchID: 0
  });
  const [externalBatchID, setExtBatchID] = useState(0);

  const [leftPaneMessage, setLeftPaneMessage] = useState("");

  const [isPageLoading, setIsPageLoading] = useState(false);

  const noteForActionRef = useRef();
  const narrativeForStateRef = useRef();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmationDialogContent, setConfirmationDialogContent] = useState([]);

  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationDialogContent, setNotificationDialogContent] = useState([]);
  const [timerNotificationDialogClose, setTimerNotificationDialogClose] = useState(5000);
  const [timerAutoCloseNotificationDialog, setTimerAutoCloseNotificationDialog] = useState(false);

  // State to track when focus should be set to case search field after notification dialog closes
  const [shouldFocusCaseSearch, setShouldFocusCaseSearch] = useState(false);

  
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");

  // Initial state for posting the fields to SAVE THE DOCUMENT
  const initialNewDocData = {
    userLoginID: 0,
    fKM_UserLoginID: 0,
    orgID: 0,
    batchStatusID: 0,
    batchID: 0,
    batchName: "",
    batchDocumentID: 0,
    docGroupID: 0,
    documentTypeID: 0,
    documentStatusID: 0,
    documentName: "",
    documentLocation: "",
    notes: "",
    narrative: "",
    yTracCaseID: 0,
    caseNumber: "",
    nocase: 0,
    indexingCompleteTS: null,
    docQueueID: 0,
    "FKM_ScanUserLoginID": null,
    indexTS: null,
    "FKM_IndexUserLoginID": null,
    "FKM_LastIndexUserLoginID": null,
    lastIndexTS: null,
    documentOrder: 0,
    yTracSiteID: 0,
    documentUNCPath: "",
    masterServerDBSchema,
    actionStartTime: null
  };

  // Array for posting the fields to SAVE THE DOCUMENT 
  const [postNewDocData, setPostNewDocData] = useState(initialNewDocData);

  const clearPostNewDocData = () => {
    try {
      const updatedDocValues = {
        ...postNewDocData,
        ...initialNewDocData
      };
      updateArrayStateValues(setPostNewDocData, updatedDocValues);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearPostNewDocData", {});
    }
  }

  const batchDocumentProcess = EnumUtility.BatchDocumentProcess;

  const defaultPermissionsForFields = {
    newDocumentBtn: { read: false, write: true, hide: false },
    batchSelect: { read: false, write: true, hide: false },
    caseNumberTxt: { read: false, write: true, hide: false },
    noCaseChk: { read: false, write: true, hide: false },
    docGroupSelect: { read: false, write: true, hide: false },
    docTypeSelect: { read: false, write: true, hide: false },
    noteForActionTxt: { read: false, write: true, hide: false },
    narrativeForStateTxt: { read: false, write: true, hide: false },
    cancelBtn: { read: false, write: true, hide: false },
    leftPaneMessageLbl: { read: false, write: true, hide: false },
    saveBtn: { read: false, write: true, hide: false },
    saveAnotherBtn: { read: false, write: true, hide: false },
    skipDocumentBtn: { read: false, write: true, hide: false },
    nextActionTypeSelect: {read: false, write: true, hide: false },
    nextActionDueDate: {read: false, write: true, hide: false }
  };
  const [formFieldAccess, setFormFieldAccess] = useState(defaultPermissionsForFields);

  const [timeActionStart, setTimeActionStart] = useState(null);
  const [timerView, setTimerView] = useState("00:00");
  const timerRef = React.useRef(null); // Keep the TimerUtils instance
  const isTimerVisible = false;

  // Initialize timer only once
  if (!timerRef.current) {
    timerRef.current = new TimerUtility(setTimerView);
  };

  const toUpperCase = (stringToUpperCase) => {
    return String(stringToUpperCase).toUpperCase();
  };

  const toLowerCase = (stringToLowerCase) => {
    return String(stringToLowerCase).toLowerCase();
  };


  const [alertProps, setAlertProps] = useState({
    type: '',
    message: '',
    open: false,
  });

  const showAlert = (type, message) => {
    setAlertProps({ type, message, open: true });
  };



 useEffect(()=>{
    const getHolidayList = async () => {
    const loginUserInfo = sessionService.getUser();
    const { siteID } = loginUserInfo || {};
    const today = getToday();
    setHolidayList(await SharedService.getHolidayList(today, siteID))
    }
    (async()=>await(getHolidayList()))();
 }, [])

  //=======================================================================
  // Tabs Feature - Begin
  //=======================================================================



  // Function to handle New Workitem Click
  const handleNewWorkItemClick = async () => {
    setIsPageLoading(true);
    try {
      await simulateAsyncDelay();
      setIsEditable(true);
      formFieldsEnableLogic(dispatch, FormFieldEnableType.NewWorkItemClick.ID);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNewWorkItemClick");
    }
    finally { setIsPageLoading(false); }
  };

  const handleReIndexClick = () => {
    setActivePage('newDocument');
    const reIndexPageProps = EnumUtility.DocumentSearchPageProperty.ReIndexDocument;

    navigate('/documentsearch', {
      state: reIndexPageProps,
    });
  };

  const clearCaseTabInfo = () => {
    if (caseInfoRef.current) {
      // Call the child method with parameters
      caseInfoRef.current.clearCaseInfoFromExternal(externalBatchID, showExtDocGrid);
    }
  };


  const onCaseSearch = async (caseNumber, caseSearchSource) => {
    try {
      console.log("onCaseSearch caseNumber: ", caseNumber);
      if (caseInfoRef.current) {
        // Call the child method with parameters
        const caseID = toUpperCase(caseNumber);
        await caseInfoRef.current.onSelectCaseFromExternal(caseID, showExtDocGrid, caseSearchSource);
      }
    }
    catch (error) {
      await LoggerUtility.ProcessErrorData(error, "onCaseSearch", {});
    }
  };

  // Function to handle search
  const handleCaseSearch = async (event) => {
    try {
      await simulateAsyncDelay();
      const isValidCaseFormat = await checkCaseNumberRegEx(formValues.caseNumberTxt);
      if (isValidCaseFormat) {
        let validCaseSearch = true;

        if (!validCaseSearch) {//TODO not sure what this does because validCaseSearch is set to true above
          await clearCaseSearchInfo();
          clearCaseTabInfo();
          handleNotificationDialog(EnumUtility.BatchDocumentProcess.CaseNumberAddedAgain);
        }
        else {
          setIsPageLoading(true);
          await onCaseSearch(formValues.caseNumberTxt, CaseSearchSource.CaseNumberField.ID);
          setWorkflowOptions(workflowCaseOptions);
        }
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseSearch");
    }
    // finally { setIsPageLoading(false); }
  };

  const clearCaseSearchInfo = async (preserveCaseNumber = false) => {
    try {
      setTimeActionStart(null);
      const updatedFormValues = {
        ...formValues,
        caseNumberTxt: preserveCaseNumber ? formValues.caseNumberTxt : "",
        yTracCaseID: 0,
        caseNumberSearched: "",
        workflowSelect: { id: -123456, name: "" },
        workflowSelectedRow: { 'workflowID': -123456, 'workflowName': "", 'enableAddTabNoCase': false, 'enableAddTabInputChannelChoice': false, 'enableAddTabOverrideStepDate': false, 'fkM_NoteTemplateID': 0, 'departmentName': "", },
        actionTypeSelect: { id: -123456, name: "" },
        actionTypeSelectedItem: { actionTypeID: -123456, workflowID: 0, actionType: "", displayOrder: 0, initialWorkflowStepID: 0, workflowStartID: 0, workflowStartTypeID: 0, workflowMapID: 0 },
        nextActionTypeSelect: { id: -123456, name: "" },
        nextActionTypeSelectedItem: { nextActionTypeID: -123456, workflowID: 0, workflowStepID: 0, workflowStepName: "", workflowMapID: 0, displayOrder: 0, departmentName: "", },
        iccSelect: { iccTypeID: -123456, dropdownName: "" },
        iccViewEnabled: false,
        nextActionDueDateTxt: null,
      };
      updateArrayStateValues(setFormValues, updatedFormValues);
      debouncedValidation(updatedFormValues, caseValidationType);
    }
    catch (error) {
      await LoggerUtility.ProcessErrorData(error, "clearCaseSearchInfo", {});
    }
  };

  // Function to handle clear of case search value
  const handleCaseClear = async () => {
    setIsPageLoading(true);
    try {
      await simulateAsyncDelay();
      await clearCaseSearchInfo();
      clearCaseTabInfo();
      
      // Apply proper field enable logic to disable workflow and related fields
      formFieldsEnableLogic(dispatch, FormFieldEnableType.NewWorkItemClick.ID);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseClear");
    }
    finally { setIsPageLoading(false); }
  };

  // Function to handle case search completion
  const handleCaseSearchComplete = async (hasValidCase, caseSearchInfo, caseSearchSource) => {
    try {
      setIsPageLoading(false);
      let updatedFormValues = formValues;

      const caseInfo = caseSearchInfo?.caseData;

      if (hasValidCase) {
        const loginUserInfo = sessionService.getUser();
        let dialogContent;

         if (caseSearchInfo?.isCaseConfidential) {
          if (caseSearchInfo?.isConfidentialForThisUser) {
            // If the case was marked confidential for THIS USER then show the message below. 
            const messageContent = await SharedService.getMessageContent(ClientType.MessageContent.N_UI7060_Confidential_Case_Permission_Warning)
            dialogContent = {
              NotificationType: "warning", Title: "Warning",
              Message: messageContent?.messageText?.replace("{0}", caseSearchInfo?.caseData?.caseNumber)?.replace(/\\n/g, "\u00A0\n") ?? ""
            }
            handleNotificationDialog(dialogContent);
            return
          }
          else {
            dialogContent = await CaseWorkflowService.createEscalationNotificationMessage(caseSearchInfo?.caseData, loginUserInfo, masterServerDBSchema)
            handleNotificationDialog(dialogContent);
          }
        }

        //timerRef.current.startTimer();
        setTimeActionStart(UtilityLibrary.getCurrentDateTimeForTimeZone(loginUserInfo?.timeZone));
        
        updatedFormValues = {
          ...formValues,
          yTracCaseID: caseInfo.yTracCaseID,
          caseNumberTxt: caseInfo.caseNumber,
          caseNumberSearched: caseInfo.caseNumber,
        }

        formFieldsEnableLogic(dispatch, FormFieldEnableType.CaseNumberValid.ID);
        // Re-enable the search icon after successful case search
        changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
        debouncedValidation(updatedFormValues, caseValidationType);
        // handleNotificationDialog(EnumUtility.ConfirmDialogType.CaseNumberFound);
        updateArrayStateValues(setFormValues, updatedFormValues);
      }
      else {
        if (siteSettingDetail.CaseNumberInsertAllow === "1") {
          setTimeActionStart(UtilityLibrary.getCurrentDateTimeForTimeZone(sessionService.getUser()?.timeZone));

          updatedFormValues = {
            ...formValues,
            yTracCaseID: 0,
            caseNumberSearched: formValues.caseNumberTxt,
          }

          formFieldsEnableLogic(dispatch, FormFieldEnableType.CaseNumberValid.ID);
          // Re-enable the search icon for manual case entry
          changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
          
          debouncedValidation(updatedFormValues, caseValidationType);
          updateArrayStateValues(setFormValues, updatedFormValues);
        }
        else {
          //timerRef.current.resetTimer();
          let shouldPreserve = false;
          let errorMessage = {};
          if (caseSearchInfo.statusCode === EnumUtility.CaseSearchResultCode.CaseNotFound.ID) {
            const loginUserInfo = sessionService.getUser();
            const userStateID = loginUserInfo ? loginUserInfo.stateID : -1;
            errorMessage = EnumUtility.getCaseNumberNotFoundError(userStateID);
            shouldPreserve = true;
            // Only show error if user is in MS/KS/WY, otherwise skip showing error
            if (!errorMessage) {
              return; // Exit without showing error for other states
            }
          }
          else if ([ EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID,
            EnumUtility.CaseSearchResultCode.ScraperDisconnectedError.ID].includes(caseSearchInfo.statusCode )) {
            let messageContentID = null;
            if (caseSearchInfo.statusCode === EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID){
              messageContentID = ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System
            }
            else{
              messageContentID = ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
            }
            const messageContent = await SharedService.getMessageContent(messageContentID)
            errorMessage = { NotificationType: 'error', Message: messageContent?.messageText?.replace(/\\n/g, "\u00A0\n") ?? "" }
            shouldPreserve = true;
          }
          else if (caseSearchInfo.statusCode === EnumUtility.CaseSearchResultCode.Enter_A_Function_Error.ID) {
            const messageContent = await SharedService.getMessageContent(
            ClientType.MessageContent.N_UI7058_ENTER_A_FUNCTION_Error_Occured
            );
            errorMessage = {
            NotificationType: "error",
            Message: messageContent?.messageText?.replace(/\n/g, "\u00A0\n") ?? ""
            };
            shouldPreserve = true;
            }
          else {
            errorMessage = { ID: 321, Name: "NetworkError", NotificationType: EnumUtility.NotificationType.Warning, Title: "Warning", Message: caseSearchInfo.errorMessage };
          }          

          // Check if case number should be preserved based on error message
          // const shouldPreserve = EnumUtility.shouldPreserveCaseNumber(errorMessage);
          
          // Only clear case info if case number should not be preserved
          if (!shouldPreserve) {
            await clearCaseSearchInfo();
          }

          if (FormFieldEnableType.BatchSelect) formFieldsEnableLogic(dispatch, FormFieldEnableType.BatchSelect.ID);
          debouncedValidation(formValues, caseValidationType);
          const dialogContent = {
            ...errorMessage,
            dynamicMessageContent: [formValues.caseNumberTxt],
          };
          handleNotificationDialog(dialogContent);
        }
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseSearchComplete");
    }
  };

  const changeCaseNumberIconsDisable = (iconsEnabledValue) => { // RC removed async as not needed
    try {
      const updatedCaseNumberIconsEnabled = {
        ...caseNumberIconsDisabled,
        search: iconsEnabledValue.search,
        copy: iconsEnabledValue.copy,
        clear: iconsEnabledValue.clear,
      };
      updateArrayStateValues(setCaseNumberIconsDisabled, updatedCaseNumberIconsEnabled);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "changeCaseNumberIconsDisable", {});
    }
  };

  const checkCaseNumberRegEx = (caseNumber) => { // RC removed async as its not needed
    try {
      let isValidFormat = true;
      const caseNumberRegexValue = new RegExp(caseNumberRegex.caseRegexValue || "");

      if ((caseNumberRegexValue !== "") && (caseNumber !== "")) {
        if (!caseNumberRegexValue.test(caseNumber)) {
          caseNumberRef.current.setValidationMessage(caseNumberRegex.caseRegexMessage);
          changeCaseNumberIconsDisable({ search: true, copy: false, clear: false });
          isValidFormat = false;
        }
        else {
          caseNumberRef.current.setValidationMessage("");
          changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
        }
      }
      else {
        caseNumberRef.current.setValidationMessage("");
        changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
      }

      return isValidFormat;
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "checkCaseNumberRegEx", {});
    }
  };

  const handleCaseChange = async (e) => {
    try {
      // RC do not make any async calls before case number 
      // state update to ensure the case number cursor stays 
      // in place while editing

      const nameText = e.target.name;
      const valueText = e.target.value;
      const loginState = SessionManager.GetValueFromSession(SessionManager.LoginStateKey);

      checkCaseNumberRegEx(valueText);

      // Update input value
      //const { name, value } = e.target;
      const updatedFormValues = {
        ...formValues,
        [nameText]: valueText,
        workflowSelect: initWorkflow,
        workflowSelectedRow: initWorkflowSelectedRow,
        actionTypeSelect: initActionTypeSelect,
        actionTypeSelectedItem: initActionTypeSelectedItem,
        nextActionTypeSelect: initNextActionTypeSelect,
        nextActionTypeSelectedItem: initNextActionTypeSelectedItem,
        iccSelect: initActionTypeSelect,
        iccViewEnabled: false,
        nextActionDueDateTxt: "",
      };
      setFormValues(updatedFormValues);
      console.log("UPDATED FORM VALUES", updatedFormValues)
      debouncedValidation(updatedFormValues, caseValidationType);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseChange");
    }
  };

  // Handler to show message after processing document in Right Pane
  const handleShowBatchDocMessage = (leftMessage) => {
    setLeftPaneMessage(leftMessage);
  };



  //useEffect(() => { setViewedCaseIDRecords(); });

  //=======================================================================
  // Tabs Feature - End
  //=======================================================================


  const simulateAsyncDelay = async () => {
    await new Promise((resolve) => setTimeout(resolve, 0));
  };

  //aa--------------------------------------------------------------------
  /* 
  * Authorization code starts here
  * The permission values for fields are received from Web API
  * The received data from Web API response is processed and stored in a state variable "formFieldAccess"
  */
  useEffect(() => {
    const fetchFormFieldPermissions = async () => {
      try {
        //TODO: Pass variables from session to the below URL
        const loginUserInfo = sessionService.getUser();
        const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
        const params = {
          clientID: loginStateID,
          formID: 1      // To-do: Modify Code - Hard-coded value of FormID: 1
        }
        const controlSecurityListApiData = await ApiManager.FetchControlsPermissions(params);
        const actualPermissionsForFields = DeterminePermissions(controlSecurityListApiData);
        console.log("ACTUAL PERMISSION", actualPermissionsForFields)
        setFormFieldAccess({ ...defaultPermissionsForFields, ...actualPermissionsForFields });
      }
      catch (error) {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchFormFieldPermissions");
      }
    };

    fetchFormFieldPermissions();
  }, []);

  //aa--------------------------------------------------------------------

  useEffect(() => {
    const fetchWorkflowOptions = async () => {
      try {
        //TODO: Pass variables from session to the below URL
        const loginUserInfo = sessionService.getUser();
        //const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
        const loginSiteID = loginUserInfo ? loginUserInfo.siteID : -123456;
        const loginUserID = loginUserInfo ? loginUserInfo.legacyUserID : -123456;
        const params = {
          YTracSiteID: loginSiteID,
          masterServerDBSchema, // To-do: Modify Code - Hard-coded value of Master DB Schema
          FKM_UserLoginID: loginUserID,
        }
        const workflowTypesApiData = await ApiManager.GetWorkflowTypeList(params);
        const desiredColumns = ['workflowID', 'workflowName', 'queueName', 'enableAddTabNoCase', 'enableAddTabInputChannelChoice', 'enableAddTabOverrideStepDate', 'workflowCategoryTypeID', 'fkM_NoteTemplateID', 'departmentName'];
        const filteredData = UtilityLibrary.GetSelectedColumns(workflowTypesApiData, desiredColumns);
        setWorkflowList(filteredData);

        // Workflow: WITH CASE
        const withCaseCondition = (item) => item.enableAddTabNoCase === false || item.enableAddTabNoCase === true;
        const withCaseWorkflowData = UtilityLibrary.filterArrayByCondition(filteredData, withCaseCondition);
        setWorkflowCaseOptions(withCaseWorkflowData);
        setWorkflowOptions(withCaseWorkflowData);

        // Workflow: WITH NO CASE
        const noCaseCondition = (item) => item.enableAddTabNoCase === true;
        const noCaseWorkflowData = UtilityLibrary.filterArrayByCondition(filteredData, noCaseCondition);
        setWorkflowNoCaseOptions(noCaseWorkflowData);
      }
      catch (error) {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchWorkflowOptions");
      }
    };

    fetchWorkflowOptions();
  }, []);


  const fetchIccListOptions = async (paramsObj) => {
    try {
      const params = {
        WorkflowID: paramsObj.workflowID,
        WorkflowStartTypeID: paramsObj.workflowStartTypeID,
      }
      const iccListApiData = await ApiManager.GetIccTypeList(params);
      const rowsWithIds = iccListApiData.map((item, index) => ({
        ...item,
        iccTypeID: (index + 1)
      }));
      setIccListOptions(rowsWithIds);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchIccListOptions");
    }
  };

  const fetchActionTypeOptions = async (workflowID) => {
    try {
      const params = {
        WorkflowID: workflowID
      }
      const actionTypesApiData = await ApiManager.GetActionTypeList(params);
      const rowsWithIds = actionTypesApiData.map((item, index) => ({
        ...item,
        actionTypeID: (index + 1)
      }));
      setActionTypeOptions(rowsWithIds);
      return rowsWithIds;

    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchActionTypeOptions");
    }
  };

  const checkSingleNextActionType = async (nextActionTypesData) => {
    try {
      let nextActionTypeSelected = "";
      let nextActionTypeSelectedItem = { nextActionTypeID: -123456, workflowID: 0, workflowStepID: 0, workflowStepName: "", workflowMapID: 0, displayOrder: 0, departmentName: "", };
      if (nextActionTypesData.length === 1) {
        nextActionTypeSelected = nextActionTypesData[0].nextActionTypeID;
        nextActionTypeSelectedItem = nextActionTypesData[0];
      }
      return { nextActionTypeSelected, nextActionTypeSelectedItem };
    }
    catch (error) {
      await LoggerUtility.ProcessErrorData(error, errorFormName, "checkSingleNextActionType");
    }
  };

  const fetchNextActionTypeOptions = async (paramsObj) => {
    const loginUserInfo = sessionService.getUser();
    const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;

    try {
      const params = {
        WorkflowID: paramsObj.workflowID,
        WorkflowStartID: paramsObj.workflowStartID,
        masterServerDBSchema,
        YTracSiteID: loginStateID,
      }
      const nextActionTypesApiData = await ApiManager.GetNextActionTypeList(params);
      const rowsWithIds = nextActionTypesApiData.map((item, index) => ({
        ...item,
        nextActionTypeID: (index + 1)
      }));
      setNextActionTypeOptions(rowsWithIds);
      return rowsWithIds;
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchNextActionTypeOptions");
    }
  };



  const clearFormStateValues = () => {
    try {
      setTimeActionStart(null);
      setFormValues((prevValues) => ({
        ...prevValues,
        ...initialFormValues
      }));
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearFormStateValues", {});
    }
  };

  const disableExternalBatchCaseInfo = () => {
    if (caseInfoRef.current) {
      // Call the child method with parameters
      caseInfoRef.current.onDisableCaseInfoForm();
    }
  };

  const clearNewWorkflowForm = () => {
    try {
      clearFormStateValues();
      noteForActionRef.current?.clear();
      narrativeForStateRef.current?.clear();
      //disableExternalBatchCaseInfo();
      setResultsListOfActions([]);
      setAreDropdownsLocked(false); // Reset dropdowns lock state

      const validationType = EnumUtility.CaseType.Case;
      setCaseValidationType(validationType);
      debouncedValidation(initialFormValues, validationType);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearNewWorkflowForm", {});
    }
  };

  const handleCancelClick = async () => {
    try {
      if (UtilityLibrary.CompareTwoObjects(formValues, initialFormValues)) {
        await cancelAndDisableForm();
      }
      else {
        handleConfirmationDialog(EnumUtility.ConfirmDialogType.NewDocumentCancel);
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCancelClick");
    }
  };

  const cancelAndDisableForm = async () => {
    //timerRef.current.resetTimer();
    setIsEditable(false);
    clearNewWorkflowForm();
  };

  

  const handleRemovActionConfirmationDialog = (primaryID) => {
    setRemoveListOfAction(primaryID);
    handleConfirmationDialog(EnumUtility.ConfirmDialogType.RemoveActionFromWorkflow);
  };

  const handleCancelConfirm = async () => {
    try {
      await cancelAndDisableForm();
      clearCaseTabInfo();

    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCancelConfirm");
    }
  };

  const handleRemoveActionConfirm = async () => {
    try {
      // setResultsListOfActions(resultsListOfActions.filter((row) => row.id !== removeListOfAction));

      setResultsListOfActions([]);
      setAreDropdownsLocked(false); // Unlock dropdowns when list is cleared
    
      // Since state updates are asynchronous, check if we're clearing the last item
      const remainingItems = resultsListOfActions.filter((row) => row.id !== removeListOfAction);
      
      
      if (remainingItems.length === 0) {
        // Re-enable fields after removing from list
        if (formValues.noCaseChk) {
          formFieldsEnableLogic(dispatch, FormFieldEnableType.NoCaseSelect.ID);
        } else if (formValues.caseNumberTxt) {
          formFieldsEnableLogic(dispatch, FormFieldEnableType.CaseNumberValid.ID);
        } else {
          formFieldsEnableLogic(dispatch, FormFieldEnableType.NewWorkItemClick.ID);
        }
        
        // Reset form values using the proper state setter
        setFormValues(prevFormValues => ({
          ...prevFormValues,
          workflowSelectedRow: initialFormValues.workflowSelectedRow,
          actionTypeSelectedItem: initialFormValues.actionTypeSelectedItem,
          nextActionTypeSelectedItem: initialFormValues.nextActionTypeSelectedItem,
          nextActionDueDateTxt: initialFormValues.nextActionDueDateTxt,
          iccSelect: "",
          iccViewEnabled: false,
          noteForActionTxt: "", // Clear the note text in form values
          narrativeForStateTxt: "", // Clear the narrative text in form values
        }));

        // Apply form field enable logic to disable the dropdowns
        formFieldsEnableLogic(dispatch, FormFieldEnableType.RemoveFromActionList.ID);

        noteForActionRef.current?.clear();
        narrativeForStateRef.current?.clear();
        
        // Recalculate required asterisks after clearing fields
        setTimeout(() => {
          const updatedFormValues = {
            ...formValues,
            noteForActionTxt: "",
            narrativeForStateTxt: "",
            workflowSelectedRow: initialFormValues.workflowSelectedRow,
            actionTypeSelectedItem: initialFormValues.actionTypeSelectedItem,
            nextActionTypeSelectedItem: initialFormValues.nextActionTypeSelectedItem,
            nextActionDueDateTxt: initialFormValues.nextActionDueDateTxt,
            iccSelect: "",
            iccViewEnabled: false,
          };
          const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues);
          setRequiredFieldLabels(requiredAsterisks);
        }, 0);
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleRemoveActionConfirm");
    }
  };

  const handleConfirmationDialog = (dialogContent) => {
    setConfirmationDialogContent(dialogContent);
    handleOpenConfirmDialog();
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleDialogConfirmAction = async (dialogContentID) => {
    try {
      switch (dialogContentID) {

        case EnumUtility.ConfirmDialogType.NewDocumentCancel.ID:
          handleCancelConfirm();
          break;

        case EnumUtility.ConfirmDialogType.RemoveActionFromWorkflow.ID:
          handleRemoveActionConfirm();
          break;

        case EnumUtility.ConfirmDialogType.SubmitCaseWithNoNarrative.ID:
          await proceedWithSave();
          break;

        default:
          break;
      }

      setConfirmDialogOpen(false);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleDialogConfirmAction");
    }
  };

  const handleNotificationDialog = (dialogContent, timerAutoClose = false, timeToClose = 5000) => {
    setNotificationDialogContent(dialogContent);
    setTimerNotificationDialogClose(timeToClose);
    setTimerAutoCloseNotificationDialog(timerAutoClose);
    handleOpenNotificationDialog();
  };

  const handleOpenNotificationDialog = () => {
    setNotificationDialogOpen(true);
  };
  

  const handleCloseNotificationDialog = useCallback(() => {
    setNotificationDialogOpen(false);
    
    // Set focus to Case Search field if a save operation just completed
    if (shouldFocusCaseSearch) {
      setShouldFocusCaseSearch(false);
      setTimeout(() => {
        if (caseNumberRef.current) {
          caseNumberRef.current.focus();
        }
      }, 100);
    }
  }, [shouldFocusCaseSearch]);

  // Auto-hide success message after 3 seconds
  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        // Set focus to Case Search field after success message disappears
        if (shouldFocusCaseSearch) {
          setShouldFocusCaseSearch(false);
          setTimeout(() => {
            if (caseNumberRef.current) {
              caseNumberRef.current.focus();
            }
          }, 100);
        }
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage, shouldFocusCaseSearch]);

  // Handle click anywhere to hide success message
  useEffect(() => {
    const handleClickAnywhere = () => {
      if (showSuccessMessage) {
        setShowSuccessMessage(false);
        // Set focus to Case Search field after success message disappears
        if (shouldFocusCaseSearch) {
          setShouldFocusCaseSearch(false);
          setTimeout(() => {
            if (caseNumberRef.current) {
              caseNumberRef.current.focus();
            }
          }, 100);
        }
      }
    };

    if (showSuccessMessage) {
      document.addEventListener('click', handleClickAnywhere);
      return () => document.removeEventListener('click', handleClickAnywhere);
    }
  }, [showSuccessMessage, shouldFocusCaseSearch]);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    const updatedFormValues = { ...formValues, [name]: checked };
    setFormValues(updatedFormValues);
    debouncedValidation(updatedFormValues, caseValidationType);
  };


  const isEmptyObject = (obj) => {
    return Object.keys(obj).length === 0;
  };


  const handleNoCaseChange = async (e) => {
    try {
      const { name, checked } = e.target;
      let validationType = EnumUtility.CaseType.NoCase;
      let workflowSelectValue = { id: -123456, name: "" };
      let actionTypeSelectValue = { id: -123456, name: "" };
      let nextActionTypeSelectValue = { id: -123456, name: "" };
      let narrativeText = "";

      if (checked) {
        //timerRef.current.startTimer();
        setTimeActionStart(UtilityLibrary.getCurrentDateTimeForTimeZone(sessionService.getUser()?.timeZone));

        setWorkflowOptions(workflowNoCaseOptions);

        formFieldsEnableLogic(dispatch, FormFieldEnableType.NoCaseSelect.ID);
        setCaseValidationType(validationType);
        caseNumberRef.current.setValidationMessage("");

        // Clear the narrative field for No Case workflows
        if (narrativeForStateRef.current) {
          narrativeForStateRef.current.setValue("");
        }

        clearCaseTabInfo();
      }
      else {
        //timerRef.current.resetTimer();        
        setTimeActionStart(null);

        setWorkflowOptions(workflowCaseOptions);

        formFieldsEnableLogic(dispatch, FormFieldEnableType.CaseNumberInValid.ID);
        validationType = EnumUtility.CaseType.Case;
        setCaseValidationType(validationType);
        
        // Clear all workflow-related selections when switching to case mode
        workflowSelectValue = { id: -123456, name: "" };
        actionTypeSelectValue = { id: -123456, name: "" };
        nextActionTypeSelectValue = { id: -123456, name: "" };
      }
      const updatedFormValues = {
        ...formValues,
        [name]: checked,
        isNoCase: checked ? 1 : 0,
        caseNumberTxt: "",
        yTracCaseID: 0,
        caseNumberSearched: "",
        narrativeForStateTxt: narrativeText,
        workflowSelect: workflowSelectValue,
        workflowSelectedRow: { 'workflowID': -123456, 'workflowName': "", 'enableAddTabNoCase': false, 'enableAddTabInputChannelChoice': false, 'enableAddTabOverrideStepDate': false, 'fkM_NoteTemplateID': 0, 'departmentName': "", },
        actionTypeSelect: actionTypeSelectValue,
        actionTypeSelectedItem: { actionTypeID: -123456, workflowID: 0, actionType: "", displayOrder: 0, initialWorkflowStepID: 0, workflowStartID: 0, workflowStartTypeID: 0, workflowMapID: 0 },
        nextActionTypeSelect: nextActionTypeSelectValue,
        nextActionTypeSelectedItem: { nextActionTypeID: -123456, workflowID: 0, workflowStepID: 0, workflowStepName: "", workflowMapID: 0, displayOrder: 0, departmentName: "", },
        nextActionDueDateTxt: "",
        iccSelect: { iccTypeID: -123456, dropdownName: "" },
        iccViewEnabled: false,
      };
      updateArrayStateValues(setFormValues, updatedFormValues);
      
      // Clear results list and reset dropdown lock state when switching modes
      setResultsListOfActions([]);
      setAreDropdownsLocked(false);
      
      // Clear text area refs
      if (noteForActionRef.current) {
        noteForActionRef.current.clear();
      }
      if (narrativeForStateRef.current) {
        narrativeForStateRef.current.clear();
      }
      
      // Ensure validation runs after form state updates
      setTimeout(() => {
        debouncedValidation(updatedFormValues, validationType);
      }, 50);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNoCaseChange");
    }
  };


  const handleAlertClose = () => {
    setAlertProps((prevState) => ({ ...prevState, open: false }));
  };



  const handleNotesChange = async (event) => {
    try {
      const { name, value } = event.target;
      const updatedFormValues = { ...formValues, [name]: value };
      setFormValues(updatedFormValues);
      updateArrayStateValues(setPostNewDocData, { notes: value });
      debouncedValidation(updatedFormValues, caseValidationType);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNotesChange");
    }
  };

  const handleNarrativeChange = async (event) => {
    try {
      const { name, value } = event.target;
      const updatedFormValues = { ...formValues, [name]: value };
      setFormValues(updatedFormValues);
      updateArrayStateValues(setPostNewDocData, { narrative: value });
      debouncedValidation(updatedFormValues, caseValidationType);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNarrativeChange");
    }
  };


  const copyToClipboardFallback = (text) => {
    // Create a temporary textarea element
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed'; // Avoid scrolling to the bottom
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0'; // Make it invisible
    document.body.appendChild(textarea);

    // Select and copy the text
    textarea.select();
    textarea.setSelectionRange(0, text.length); // Ensure all text is selected
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        console.log('Text successfully copied to clipboard');
        //alert('Text copied to clipboard!'); // Notify the user
      } else {
        throw new Error('Failed to copy text using execCommand');
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      //alert('Failed to copy text. Please try again.'); // Notify the user
    }

    // Clean up
    document.body.removeChild(textarea);
  };

  const copyToClipboard = async (textToCopy) => {
    //const textToCopy = textareaRef.current.value;

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(textToCopy)
        .then(() => {
          console.log('Text successfully copied to clipboard');
          //alert('Text copied to clipboard!'); // Optional: Notify the user
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
          // Fallback to execCommand if Clipboard API fails
          copyToClipboardFallback(textToCopy);
        });
    } else {
      // Use fallback if Clipboard API is not supported
      copyToClipboardFallback(textToCopy);
    }
  };

  const onLoadNarrativeForWorkflow = async () => {
    try {
      let narrativeText = "";
      
      // Skip narrative generation for No Case workflows
      if (formValues.noCaseChk) {

        if (narrativeForStateRef.current) {
          narrativeForStateRef.current.setValue("");
        }
        
        const syntheticEvent = {
          target: {
            name: 'narrativeForStateTxt',
            value: ""
          }
        };
        await handleNarrativeChange(syntheticEvent);
        return;
      }
      
      const loginUserInfo = sessionService.getUser();
      const loginLegacyUserID = loginUserInfo ? loginUserInfo.legacyUserID : -123456;
      const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
      const loginSiteID = loginUserInfo ? loginUserInfo.siteID : -123456;
      const userLogin = JSON.parse(SessionManager.GetValueFromSession(SessionManager.UserLoginDetailsKey));
      
      const paramsForRequest = {
        ytracSiteID: loginSiteID,
        workflowTypeName: formValues.workflowSelectedRow.workflowName,
        fKM_NoteTemplatedID: formValues.workflowSelectedRow.fkM_NoteTemplateID,
        actionTypeName: formValues.actionTypeSelectedItem.actionType,
        userLoginID: loginLegacyUserID,
        masterServerDBSchema,
        yTracSite_StateID: loginStateID,
        workflowStartID: formValues.workflowStartID,
        loginName: userLogin ? userLogin.loginName?.toLowerCase() : "",
        SystemTimeZoneName: loginUserInfo?.timeZone,
      };

      const narrativeApiData = await ApiManager.GetWorkflowNarrative(paramsForRequest);
      if (narrativeApiData && narrativeApiData.length > 0) {
        narrativeText = narrativeApiData[0];
      }

      // Create a example of using the Multipart_Narrative_Consolidator.
      // Assume you have two queued actions and each has a narrative that you want to consolidate into one. The narratives are as follows:
      // Other called CCenter at 12:47:24 PM about `Money questions/Direct Deposit: Message as follows:
      // Other called CCenter at 12:47:24 PM about `Enforcement/New info on NCP employ: Action needed: Referred for Follow Ups-Mail Out-Payment History
      // Result should be:
      // Other called CCenter at 12:47:24 PM about Money questions/Direct Deposit: Message as follows:, Enforcement/New info on NCP employ: Action needed: Referred for Follow Ups-Mail Out-Payment History

      // Example use of Multipart_Narrative_Consolidator
      const exampleNarratives = [
      "Other called CCenter at 12:47:24 PM about " + String.fromCharCode(96) + "Money questions/Direct Deposit: Message as follows:",
      "Other called CCenter at 12:47:24 PM about " + String.fromCharCode(96) + "Enforcement/New info on NCP employ: Action needed: Referred for Follow Ups-Mail Out-Payment History"
      ];

      const consolidatedExample = UtilityLibrary.Multipart_Narrative_Consolidator(exampleNarratives);


      if (narrativeForStateRef.current) {
        narrativeForStateRef.current.setValue(narrativeText);
      }
      
      // Manually trigger the handleNarrativeChange to update form state and validation
      const syntheticEvent = {
        target: {
          name: 'narrativeForStateTxt',
          value: narrativeText
        }
      };
      await handleNarrativeChange(syntheticEvent);

      console.log('Narrative: ' + narrativeText);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "onLoadNarrativeForWorkflow", {});
    }
  };

  const handleAddToListClick = async (event) => {
    try {
      // Small delay to ensure form values are fully updated from previous state changes
      await new Promise(resolve => setTimeout(resolve, 10));
      await onLoadNarrativeForWorkflow();
      listOfActionsGridIndex = (listOfActionsGridIndex + 1);
      const workflowName = formValues.workflowSelectedRow?.workflowName || "";
      const queueName = formValues.workflowSelectedRow?.queueName || "";
      const listOfAction = {
        id: listOfActionsGridIndex,
        action: formValues.actionTypeSelectedItem.actionType,
        workflow: workflowName,
        queue: queueName,
        daystoNext: formValues.nextActionDueDateTxt
          ? calculateBusinessDaysBetween(formValues.nextActionDueDateTxt, holidayList)
          : (siteSettingDetail.DaysToNextFollowup || 0),
        nextActionType: "",
      };
      const listOfActionsGridRow = [
        listOfAction,
      ];
      setResultsListOfActions(listOfActionsGridRow);
      setAreDropdownsLocked(true); // Lock dropdowns after adding to list
      changeCaseNumberIconsDisable({ search: true, copy: false, clear: true });
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleAddToListClick");
    }
  };

  
  const onCopyFormDataToPostData = async () => {
    try {
      const loginUserInfo = sessionService.getUser();
      const loginLegacyUserID = loginUserInfo ? loginUserInfo.legacyUserID : -123456;
      const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
      const loginSiteID = loginUserInfo ? loginUserInfo.siteID : -123456;
      const timeZone = loginUserInfo.timeZone;
      const workflowMapID = formValues.nextActionTypeSelectedItem.workflowMapID || formValues.actionTypeSelectedItem.workflowMapID || 0;
      const startWorkflowStepID = formValues.nextActionTypeSelectedItem.workflowStepID || formValues.actionTypeSelectedItem.initialWorkflowStepID || 0;
      const hasSelectedNAT = formValues.nextActionTypeSelectedItem?.nextActionTypeID > 0;
      const postNewData = {
        "workflowID": formValues.workflowSelectedRow.workflowID,
        "caseNumber": formValues.caseNumberSearched,
        "isNocase": formValues.isNoCase,
        "notes": formValues.noteForActionTxt,
        "narrative": formValues.narrativeForStateTxt,
        "yTracSiteID": loginSiteID,
        "masterServerDBSchema": masterServerDBSchema,
        "actionStartTime": timeActionStart,
        "userLoginID": loginLegacyUserID,
        "startWorkflowStepID": startWorkflowStepID,
        "workflowMapID": workflowMapID,
        "yTracSite_StateID": loginStateID,
        "nextStepDueTS": hasSelectedNAT ? formValues.nextActionDueDateTxt : null,
        "moduleID": 1, // To-do: Modify Code - Change Hard-Coded value of ModuleID
        "systemTimeZoneName": timeZone,
        "contactID": null, // G4 New Work Item must not generate a generic Contact ID
      };

      return postNewData;
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "onCopyFormDataToPostData", {});
    }
  };

  const handleSaveClick = async (event) => {
    console.log('Save Clicked');
    setIsPageLoading(true);
    try {
      const narrativeText = narrativeForStateRef.current ? narrativeForStateRef.current?.getValue() : "";
      
      // Check if this is a case (not no-case) and narrative is empty
      const isNarrativeEmpty = !narrativeText || narrativeText.trim() === "";
      
      if (!formValues.noCaseChk && isNarrativeEmpty) {
        setIsPageLoading(false); // Turn off loading since we're showing dialog
        // Show confirmation dialog for submitting case with no narrative
        handleConfirmationDialog(EnumUtility.ConfirmDialogType.SubmitCaseWithNoNarrative);
        return;
      }
      
      // Proceed with save
      await proceedWithSave();
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSaveClick");
    }
  };

  const proceedWithSave = async () => {
    try {
      setIsPageLoading(true);

      const currentDateTime = new Date().toISOString();
      const loginUserInfo = sessionService.getUser();
      const loginLegacyUserID = loginUserInfo ? loginUserInfo.legacyUserID : -123456;
      const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
      const loginSiteID = loginUserInfo ? loginUserInfo.siteID : -123456;
      
      const isMainframeIntegration = UtilityLibrary.DetermineIfMainframeHost();
  
      if (isMainframeIntegration && formValues.noCaseChk === false) {
        if (UtilityLibrary.isUsingMockData() == false) {
          const canContinue = await UtilityLibrary.checkMainframeConnectivityWithErrorHandling(
            configLibrary.apiMainframeUrl,
            handleNotificationDialog
          );

          if (!canContinue) {
            setIsPageLoading(false);
            return; // Exit early if mainframe is not reachable
          }
        }
      }

      const narrativeText = narrativeForStateRef.current ? narrativeForStateRef.current?.getValue() : "";
      await copyToClipboard(narrativeText);
      await simulateAsyncDelay();


      // TODO: Modify Code to Pass right values to the variables below

      const postNewWorkflowData = await onCopyFormDataToPostData();
      console.log(`postNewWorkflowData : ${JSON.stringify(postNewWorkflowData)}`);
      const postResponseData = await ApiManager.PostNewWorkflowSaveToApi(postNewWorkflowData);
      console.log(`postResponseData : ${JSON.stringify(postResponseData)}`);
      if (postResponseData) {

        let displayMessage = {};
        if (!formValues.noCaseChk) {
          displayMessage = {
            ...EnumUtility.WorkflowProcess.NewWorkflowForCaseSavedSuccess,
            dynamicMessageContent: [formValues.workflowSelectedRow.workflowName, formValues.caseNumberSearched],
          };
        }
        else {
          displayMessage = {
            ...EnumUtility.WorkflowProcess.NewWorkflowWithNoCaseSavedSuccess,
            dynamicMessageContent: [formValues.workflowSelectedRow.workflowName],
          };
        }

        // Format the message text without "Success" prefix
        let messageText = displayMessage.Message;
        if (displayMessage.dynamicMessageContent) {
          displayMessage.dynamicMessageContent.forEach((content, index) => {
            messageText = messageText.replace(`{${index}}`, content);
          });
        }
        
        // Show custom success message
        setSuccessMessageText(messageText);
        setShowSuccessMessage(true);

        clearNewWorkflowForm();
        clearCaseTabInfo();

        formFieldsEnableLogic(dispatch, FormFieldEnableType.NewWorkItemClick.ID);
        
        // Set flag to focus Case Search field after success message disappears
        setShouldFocusCaseSearch(true);
      }
      //timerRef.current.resetTimer();
    }
    catch (error) {//TODO call to mainframe has not been implemented yet
      const errorType = error?.Details?.type;
      if ([ClientType.ErrorType.MainframeDisconnected,
      ClientType.ErrorType.ScraperDisconnected].includes(errorType)) {
        let messageContentID = null;
        if (errorType === ClientType.ErrorType.MainframeDisconnected) {
          messageContentID = ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System
        }
        else {
          messageContentID = ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
        }
        const messageContent = await SharedService.getMessageContent(messageContentID);
        handleNotificationDialog({ NotificationType: 'error', Message: messageContent?.messageText });
      }
      else {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSaveClick");
      }
    }
    finally {
      setIsPageLoading(false); // Disable the loader
    }
  };
  
  const handleWorkflowChange = async (e, newValue, reason) => {
    try {
      //const { name, value } = e.target;
      let selectedValue = "";
      let actionTypeSelectedValue = "";
      let actionTypeSelectedItemValue = initActionTypeSelectedItem;
      let nextActionTypeSelectedValue = "";
      let nextActionTypeSelectedItemValue = initNextActionTypeSelectedItem;
      let isIccViewEnabled = false;
      let workflowStartIDValue = 0;

      if (reason !== "clear" && newValue) {
        selectedValue = newValue.workflowID;
        isIccViewEnabled = newValue.enableAddTabInputChannelChoice;
        if ([ClientType.WorkflowCategoryType.Follow_Ups,
        ClientType.WorkflowCategoryType.Complaints].includes(newValue.workflowCategoryTypeID)) {
          setDisableNextAction(true)
        }
        else {
          setDisableNextAction(false)
        }
      }
      //setWorkflowSelected(selectedValue);

      if (reason === "clear" || selectedValue === "") {
        
        const updatedFormValues = {
          ...formValues,
          workflowSelect: initWorkflow,
          workflowSelectedRow: initWorkflowSelectedRow,
          actionTypeSelect: initActionTypeSelect,
          actionTypeSelectedItem: initActionTypeSelectedItem,
          iccSelect: initICCSelect,
          iccViewEnabled: false,
          nextActionTypeSelect: initNextActionTypeSelect,
          nextActionTypeSelectedItem: initNextActionTypeSelectedItem,
          nextActionDueDateTxt: '',
          workflowStartID: 0,
        }

        setActionTypeOptions([]);
        setIccListOptions([]);
        setNextActionTypeOptions([]);

        UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);

        // Keep only workflow enabled
        formFieldsEnableLogic(dispatch, FormFieldEnableType.CaseNumberValid.ID);

        const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues);
        setRequiredFieldLabels(requiredAsterisks);
        debouncedValidation(updatedFormValues, caseValidationType);
        

//         (_workflowCategoryTypeID == ClientType.WorkflowCategoryType.Follow_Ups)

// (_workflowCategoryTypeID == ClientType.WorkflowCategoryType.Complaints)

        return;
      }
      else {
        const actionTypeData = await fetchActionTypeOptions(selectedValue);

        if (actionTypeData.length === 1) {
          //nextActionTypeSelectedValue = await onLoadActionType(newValue, actionTypeData[0]);
          const  { workflowStartID, nextActionTypeSelected, nextActionTypeSelectedItem } = await onLoadActionType(newValue, actionTypeData[0]);
          actionTypeSelectedValue = actionTypeData[0].actionTypeID;
          actionTypeSelectedItemValue = actionTypeData[0];
          nextActionTypeSelectedValue = nextActionTypeSelected;
          nextActionTypeSelectedItemValue = nextActionTypeSelectedItem;
          workflowStartIDValue = workflowStartID;
        }
        else {
          formFieldsEnableLogic(dispatch, FormFieldEnableType.WorkflowSelect.ID);
        }
      }

      const updatedFormValues = {
        ...formValues,
        workflowSelect: selectedValue,
        workflowSelectedRow: newValue || initWorkflowSelectedRow,
        actionTypeSelect: actionTypeSelectedValue,
        actionTypeSelectedItem: actionTypeSelectedItemValue,
        nextActionTypeSelect: nextActionTypeSelectedValue,
        nextActionTypeSelectedItem: nextActionTypeSelectedItemValue,
        iccSelect: initICCSelect,
        iccViewEnabled: isIccViewEnabled,
        workflowStartID: workflowStartIDValue,
      };
      
      const canEnableNextActionDue = areAllRequiredDropdownsFilled(updatedFormValues);

      // Check if all required dropdowns are now filled and auto-populate date
      if (selectedValue && selectedValue !== "" && canEnableNextActionDue) {
        updatedFormValues.nextActionDueDateTxt = autoPopulateNextActionDate();
      } else {
        // Clear next action due date when Workflow (or any prerequisite) is cleared/unmet
        updatedFormValues.nextActionDueDateTxt = null;
      }

      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: canEnableNextActionDue } });
      
      UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleWorkflowChange");
    }
  };

  const onLoadActionType = async (workflowSelectedValue, actionTypeSelectedValue) => {
    try {
      let nextActionTypeSelectedValue = "";
      let nextActionTypeSelectedItemValue = initNextActionTypeSelectedItem;
      let nextActionEdit = workflowSelectedValue.enableAddTabOverrideStepDate;
      let workflowStartIDValue = 0;

      const formConfig = {
        isNextActionEditEnabled: nextActionEdit,
      };
      if (workflowSelectedValue.enableAddTabInputChannelChoice) {
        const paramsICC = {
          workflowID: actionTypeSelectedValue.workflowID,
          workflowStartTypeID: actionTypeSelectedValue.workflowStartTypeID,
        }
        await fetchIccListOptions(paramsICC);
        formFieldsEnableLogic(dispatch, FormFieldEnableType.ActionTypeSelectWithICC.ID, formConfig);
      }
      else {
        workflowStartIDValue = actionTypeSelectedValue.workflowStartID;
        const paramsNAT = {
          workflowID: actionTypeSelectedValue.workflowID,
          workflowStartID: actionTypeSelectedValue.workflowStartID,
        }
        const nextActionApiData = await fetchNextActionTypeOptions(paramsNAT);
        const { nextActionTypeSelected, nextActionTypeSelectedItem } = await checkSingleNextActionType(nextActionApiData);
        if (nextActionTypeSelected === "") {
          const nextActionItem = nextActionApiData.find(item => item["workflowStepID"] === actionTypeSelectedValue.initialWorkflowStepID);
          nextActionTypeSelectedValue = nextActionItem ? nextActionItem.nextActionTypeID : "";
          if(nextActionItem) {
            nextActionTypeSelectedItemValue = nextActionItem;
          }
        }
        else {
          nextActionTypeSelectedValue = nextActionTypeSelected;
          nextActionTypeSelectedItemValue = nextActionTypeSelectedItem;
        }
        formFieldsEnableLogic(dispatch, FormFieldEnableType.ActionTypeSelect.ID, formConfig);
      }
      return { workflowStartID: workflowStartIDValue, nextActionTypeSelected: nextActionTypeSelectedValue, nextActionTypeSelectedItem: nextActionTypeSelectedItemValue, };
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "onLoadActionType", {});
    }
  };

  const handleActionTypeChange = async (e, newValue, reason) => {
    try {
      //const { name, value } = e.target;
      let selectedValue = "";
      let actionTypeSelectedItemValue = initActionTypeSelectedItem;
      let nextActionTypeSelectedValue = "";
      let nextActionTypeSelectedItemValue = initNextActionTypeSelectedItem;
      let workflowStartIDValue = 0;

      if (reason !== "clear" && newValue) {
        selectedValue = newValue.actionTypeID;
        actionTypeSelectedItemValue = newValue;
      }
      //setWorkflowSelected(selectedValue);

      if (reason === "clear" || selectedValue === "") {
        const shouldShowIcc = !!formValues.workflowSelectedRow?.enableAddTabInputChannelChoice;

        const updatedFormValues = {
          ...formValues,
          actionTypeSelect: initActionTypeSelect,
          actionTypeSelectedItem: initActionTypeSelectedItem,
          iccSelect: initICCSelect,
          iccViewEnabled: shouldShowIcc,
          nextActionTypeSelect: initNextActionTypeSelect,
          nextActionTypeSelectedItem: initNextActionTypeSelectedItem,
          nextActionDueDateTxt: '',
          workflowStartID: 0,
        }

        setIccListOptions([]);
        setNextActionTypeOptions([]);

        UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);

        formFieldsEnableLogic(dispatch, FormFieldEnableType.WorkflowSelect.ID);

        const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues);
        setRequiredFieldLabels(requiredAsterisks);
        debouncedValidation(updatedFormValues, caseValidationType);
        return;
      }
      else {
        const workflowSelected = formValues.workflowSelectedRow;
        //nextActionTypeSelectedValue = await onLoadActionType(workflowSelected, newValue);
        const { workflowStartID, nextActionTypeSelected, nextActionTypeSelectedItem } = await onLoadActionType(workflowSelected, newValue);
        workflowStartIDValue = workflowStartID;
        nextActionTypeSelectedValue = nextActionTypeSelected;
        nextActionTypeSelectedItemValue = nextActionTypeSelectedItem;
        
      }

      const updatedFormValues = {
        ...formValues,
        actionTypeSelect: selectedValue,
        actionTypeSelectedItem: actionTypeSelectedItemValue,
        nextActionTypeSelect: nextActionTypeSelectedValue,
        nextActionTypeSelectedItem: nextActionTypeSelectedItemValue,
        iccSelect: { iccTypeID: -123456, dropdownName: "" },
        workflowStartID: workflowStartIDValue,
      };
      
      const canEnableNextActionDue = areAllRequiredDropdownsFilled(updatedFormValues);

      // Check if all required dropdowns are now filled and auto-populate date
      if (selectedValue && selectedValue !== "" && canEnableNextActionDue) {
        updatedFormValues.nextActionDueDateTxt = autoPopulateNextActionDate();
      } else {
        // Clear next action due date when Action Type (or any prerequisite) is cleared/unmet
        updatedFormValues.nextActionDueDateTxt = null;
      }

      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: canEnableNextActionDue } });
      
      UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleActionTypeChange");
    }
  };

  const handleIccTypeChange = async (e, newValue, reason) => {
    try {
      //const { name, value } = e.target;      
      let nextActionEdit = formValues.workflowSelectedRow.enableAddTabOverrideStepDate;

      const formConfig = {
        isNextActionEditEnabled: nextActionEdit,
      };
      let selectedValue = "";
      let nextActionTypeSelectedValue = "";
      let nextActionTypeSelectedItemValue = initNextActionTypeSelectedItem;
      let workflowStartIDValue = 0;
      
      if (reason !== "clear" && newValue) {
        selectedValue = newValue.iccTypeID;
      }

      if (reason === "clear" || selectedValue === "") {
        const shouldShowIcc = !!formValues.workflowSelectedRow?.enableAddTabInputChannelChoice;

        const updatedFormValues = {
          ...formValues,
          iccSelect: initICCSelect,
          iccViewEnabled: shouldShowIcc,
          nextActionTypeSelect: initNextActionTypeSelect,
          nextActionTypeSelectedItem: initNextActionTypeSelectedItem,
          nextActionDueDateTxt: '',
          workflowStartID: 0,
        }

        setNextActionTypeOptions([]);

        UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);

        formFieldsEnableLogic(dispatch, FormFieldEnableType.ActionTypeSelectWithICC.ID, formConfig);

         dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: false } });
        const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues);
        setRequiredFieldLabels(requiredAsterisks);
        debouncedValidation(updatedFormValues, caseValidationType);
        return;
      }
      else {
        workflowStartIDValue = newValue.workflowStartID;
        const paramsNAT = {
          workflowID: newValue.workflowID,
          workflowStartID: newValue.workflowStartID,
        }
        const nextActionApiData = await fetchNextActionTypeOptions(paramsNAT);
        const { nextActionTypeSelected, nextActionTypeSelectedItem } = await checkSingleNextActionType(nextActionApiData);
        nextActionTypeSelectedValue = nextActionTypeSelected;
        nextActionTypeSelectedItemValue = nextActionTypeSelectedItem;
        formFieldsEnableLogic(dispatch, FormFieldEnableType.IccSelect.ID, formConfig);
      }

      const updatedFormValues = {
        ...formValues,
        iccSelect: newValue || { iccTypeID: -123456, dropdownName: "" },
        nextActionTypeSelect: nextActionTypeSelectedValue,
        nextActionTypeSelectedItem: nextActionTypeSelectedItemValue,
        workflowStartID: workflowStartIDValue,
      };
      
      const canEnableNextActionDue = areAllRequiredDropdownsFilled(updatedFormValues);

      // Check if all required dropdowns are now filled and auto-populate date
      if (selectedValue && selectedValue !== "" && canEnableNextActionDue) {
        updatedFormValues.nextActionDueDateTxt = autoPopulateNextActionDate();
      } else {
        // Clear next action due date when ICC (or any prerequisite) is cleared/unmet
        updatedFormValues.nextActionDueDateTxt = null;
      }

      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: canEnableNextActionDue } });
      
      UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleActionTypeChange");
    }
  };

  // Helper function to check if all required dropdowns are filled
  const areAllRequiredDropdownsFilled = (currentFormValues) => {
    // Check if case number is entered OR no case is checked
    const hasCaseOrNoCase = currentFormValues.caseNumberTxt?.trim() || currentFormValues.noCaseChk;
    
    // Check if workflow is selected
    const hasWorkflow = currentFormValues.workflowSelect && 
                       currentFormValues.workflowSelect !== -123456 && 
                       currentFormValues.workflowSelect !== "";
    
    // Check if action type is selected
    const hasActionType = currentFormValues.actionTypeSelectedItem?.actionTypeID && 
                         currentFormValues.actionTypeSelectedItem.actionTypeID !== -123456;
    
    // Check if ICC is selected (only required if ICC is enabled for this workflow)
    const hasRequiredIcc = !currentFormValues.iccViewEnabled || 
                          (currentFormValues.iccSelect?.iccTypeID && 
                           currentFormValues.iccSelect.iccTypeID !== -123456);
    
    // Check if next action type is selected
    const hasNextActionType = currentFormValues.nextActionTypeSelectedItem?.nextActionTypeID && 
                             currentFormValues.nextActionTypeSelectedItem.nextActionTypeID !== -123456;
    
    return hasCaseOrNoCase && hasWorkflow && hasActionType && hasRequiredIcc && hasNextActionType;
  };

  const calculateBusinessDaysBetween = (endDateStr, holidays) => {
    if (!endDateStr) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = endDateStr.split('-').map(Number);
    const end = new Date(y, m - 1, d);
    end.setHours(0, 0, 0, 0);
    if (end <= today) return 0;
    const holidayTimestamps = new Set(
      (holidays || []).map(h => {
        const hd = new Date(h);
        hd.setHours(0, 0, 0, 0);
        return hd.getTime();
      })
    );
    let count = 0;
    const current = new Date(today);
    current.setDate(current.getDate() + 1);
    while (current <= end) {
      const day = current.getDay();
      if (day !== 0 && day !== 6 && !holidayTimestamps.has(current.getTime())) {
        count++;
      }
      current.setDate(current.getDate() + 1);
    }
    return count;
  };

  // Helper function to auto-populate next action date with today's date
  const autoPopulateNextActionDate = () => {
    const today = new Date();
    today.setHours(0,0,0,0)
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const hour = String(today.getHours()).padStart(2, "0");
    const minute = String(today.getMinutes()).padStart(2, "0");
    const second = String(today.getSeconds()).padStart(2, "0");
    const milliSecond = String(today.getMilliseconds()).padStart(3, "0");
    const formattedDate = `${year}-${month}-${day}T${hour}:${minute}:${second}.${milliSecond}`;
    //YYYY-MM-DDTHH:MM:SS.mmm
    return formattedDate;
  };

  const handleNextActionTypeChange = async (e, newValue, reason) => {
    try {
      let selectedValue = "";
      let nextActionTypeSelectedItemValue = initNextActionTypeSelectedItem;

      if (reason !== "clear" && newValue) {
        selectedValue = newValue.nextActionTypeID;
        nextActionTypeSelectedItemValue = newValue;
      }

      if (reason === "clear" || selectedValue === "") {
        const shouldShowIcc = !!formValues.workflowSelectedRow?.enableAddTabInputChannelChoice;

        const hasActionType = formValues.actionTypeSelectedItem?.actionTypeID > 0;

        const updatedFormValues = {
          ...formValues,
          // iccSelect: initICCSelect,
          // iccViewEnabled: shouldShowIcc,
          nextActionTypeSelect: initNextActionTypeSelect,
          nextActionTypeSelectedItem: initNextActionTypeSelectedItem,
          workflowStartID: 0,
          nextActionDueDateTxt: "",
        }

        UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);

        // Next Action Type stays enabled because user can re-pick it
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionTypeSelect", isEnabled: true } });

        // Date is disabled until NAT is selected again
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: false } });

        dispatch({ type: "SET_FIELD_VISIBILITY", payload: { field: "iccSelect", isVisible: shouldShowIcc && hasActionType } });

        const requiredAsterisks = calculateRequiredAsterisks(updatedFormValues);
        setRequiredFieldLabels(requiredAsterisks);
        debouncedValidation(updatedFormValues, caseValidationType);

        return;
      }

      const updatedFormValues = {
        ...formValues,
        nextActionTypeSelect: selectedValue,
        nextActionTypeSelectedItem: nextActionTypeSelectedItemValue,
      };
      
      const canEnableNextActionDue = areAllRequiredDropdownsFilled(updatedFormValues);

      // Check if all required dropdowns are now filled and auto-populate date
      if (selectedValue && selectedValue !== "" && canEnableNextActionDue) {
        updatedFormValues.nextActionDueDateTxt = autoPopulateNextActionDate();
      } else {
        // Clear next action due date when Next Action Type (or any prerequisite) is cleared/unmet
        updatedFormValues.nextActionDueDateTxt = null;
      }
      
      UtilityLibrary.updateArrayStateValues(setFormValues, updatedFormValues);
      
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateTxt", isEnabled: canEnableNextActionDue } });
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNextActionTypeChange");
    }
  };


  const handleGridFilterChange = async (newFilterModel) => {
    try {
      setGridFilterModel(newFilterModel);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleGridFilterChange");
    }
  };

  const handleOkClick = async () => {
     await cancelAndDisableForm();
  }

  const parseDate = (dateString) => {
    if (!dateString) return null;
    const dateParts = dateString.split('T')[0].split("-");
    return new Date(Number(dateParts[0]), Number(dateParts[1])-1, Number(dateParts[2]))
  }


  const leftPaneContent = (
    <div className="content-main-container">
      {!isEditable && (
        <div className="banner-blue">
          <div className="button-group">
            <div className="base-wrapper">
              {!(formFieldAccess.newDocumentBtn?.hide ?? false) && (
                <div className="base-2">
                  <img className="masked-icon" alt="Masked icon" src='/doc-New.png' />
                  <Button id="newDocumentBtn" name="newDocumentBtn" className="button-2" onClick={handleNewWorkItemClick} disabled={!(formFieldAccess.newDocumentBtn?.write ?? true)}>NEW WORK ITEM</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="pane-wrap-container">
        <div className={`${isEditable ? 'pane-main-container' : 'pane-main-container-2 greyed-out'}`}>
          <div className={`${isPageLoading ? ' greyed-out' : isEditable ? 'form-section editable' : 'form-section-2 '}`}>
            <div className="text-wrapper-header-bold">New Work Item</div>
            <div className={`${isEditable ? 'form-main-container' : 'form-main-container-2'}`}>
              {/* Header Section */}
              <header className="form-header">

              </header>
              {/* Form Section */}
              <div className='form-content'>
                <div className='form-dynamic-content'>
                  <form className="form-dynamic-grid">
                    {/* Field 1 */}

                    <div className='form-row'>
                      <div className='form-group'>
                        {!(formFieldAccess.caseNumberTxt?.hide ?? false) && (
                          <TextFieldWithIconButton
                            ref={caseNumberRef}
                            nameForField="caseNumberTxt"
                            onIconButtonClick={handleCaseSearch}
                            onClearButtonClick={handleCaseClear}
                            onTextChange={handleCaseChange}
                            valueForField={formValues.caseNumberTxt}
                            labelForField="Case Search"
                            iconsTooltipText={{ search: 'Search Case', copy: 'Copy Case', clear: 'Clear Case' }}
                            iconsDisabled={caseNumberIconsDisabled}
                            isDisabled={!formState.caseNumberTxt || areDropdownsLocked}
                            requiredFieldLabel={requiredFieldLabels.caseNumberTxt}
                          />
                        )}
                      </div>
                      <div className='form-group'>
                        {!(formFieldAccess.noCaseChk?.hide ?? false) && (
                          <div>
                            <FormControlLabel
                              name="noCaseChk"
                              control={<Checkbox />}
                              label="No Case"
                              checked={formValues.noCaseChk}
                              onChange={handleNoCaseChange}
                              disabled={!formState.noCaseChk || areDropdownsLocked}
                            />
                            <label>
                              <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.noCaseChk}</span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-wrapper-header-bold-2" style={{ marginTop: '5px' }}>Work Item Details</div>
                    <div className='form-row'>
                      <div className='form-group'>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FormControl fullWidth>
                            <Autocomplete
                              options={workflowOptions}
                              getOptionLabel={(option) => option.workflowName || ""}
                              isOptionEqualToValue={(option, value) => option.workflowID === value?.workflowID}
                              id="workflowSelect"
                              value={formValues.workflowSelectedRow?.workflowID && formValues.workflowSelectedRow.workflowID !== -123456
                                ? workflowOptions.find((opt) => opt.workflowID === formValues.workflowSelectedRow.workflowID) || null
                                : null}
                              disabled={!formState.workflowSelect || workflowOptions.length < 1 || areDropdownsLocked}
                              onChange={(event, newValue, reason) => {
                                const modifiedEvent = {
                                  target: {
                                    name: "workflowSelect",
                                  },
                                };
                                handleWorkflowChange(modifiedEvent, newValue, reason);
                              }}
                              renderInput={(params) => (
                                <TextField {...params}
                                  label="Workflow"
                                  variant="outlined"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                          <label>
                            <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.workflowSelect}</span>
                          </label>
                        </div>

                      </div>
                      <div className='form-group'>

                      </div>
                    </div>

                    <div className='form-row'>
                      <div className='form-group'>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FormControl fullWidth>
                            <Autocomplete
                              options={actionTypeOptions}
                              getOptionLabel={(option) => option.actionType || ""}
                              isOptionEqualToValue={(option, value) => option.actionTypeID === value?.actionTypeID}
                              id="actionTypeSelect"
                              value={formValues.actionTypeSelectedItem?.actionTypeID && formValues.actionTypeSelectedItem.actionTypeID !== -123456
                                ? actionTypeOptions.find((opt) => opt.actionTypeID === formValues.actionTypeSelectedItem.actionTypeID) || null
                                : null}
                              disabled={!formState.actionTypeSelect || actionTypeOptions.length <= 1 || areDropdownsLocked}
                              onChange={(event, newValue, reason) => {
                                const modifiedEvent = {
                                  target: {
                                    name: "actionTypeSelect",
                                  },
                                };
                                handleActionTypeChange(modifiedEvent, newValue, reason);
                              }}
                              renderInput={(params) => (
                                <TextField {...params}
                                  label="Action Type"
                                  variant="outlined"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                          <label>
                            <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.actionTypeSelect}</span>
                          </label>
                        </div>
                      </div>
                      <div className='form-group'>
                        {formValues.iccViewEnabled && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FormControl fullWidth>
                              <Autocomplete
                                options={iccListOptions}
                                getOptionLabel={(option) => option.dropdownName || ""}
                                isOptionEqualToValue={(option, value) => option.iccTypeID === value?.iccTypeID}
                                id="iccSelect"
                                value={formValues.iccSelect?.iccTypeID && formValues.iccSelect.iccTypeID !== -123456
                                  ? iccListOptions.find((opt) => opt.iccTypeID === formValues.iccSelect.iccTypeID) || null
                                  : null}
                                disabled={!formState.iccSelect || iccListOptions.length <= 1 || areDropdownsLocked}
                                onChange={(event, newValue, reason) => {
                                  const modifiedEvent = {
                                    target: {
                                      name: "iccSelect",
                                    },
                                  };
                                  handleIccTypeChange(modifiedEvent, newValue, reason);
                                }}
                                renderInput={(params) => (
                                  <TextField {...params}
                                    label="ICC"
                                    variant="outlined"
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                  />
                                )}
                              />
                            </FormControl>
                            <label>
                              <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.iccSelect}</span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>


                    <div className='form-row'>
                      <div className='form-group'>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FormControl fullWidth>
                            <Autocomplete
                              options={nextActionTypeOptions}
                              getOptionLabel={(option) => option.workflowStepName || ""}
                              isOptionEqualToValue={(option, value) => option.nextActionTypeID === value?.nextActionTypeID}
                              id="nextActionTypeSelect"
                              value={formValues.nextActionTypeSelectedItem?.nextActionTypeID && formValues.nextActionTypeSelectedItem.nextActionTypeID !== -123456
                                ? nextActionTypeOptions.find((opt) => opt.nextActionTypeID === formValues.nextActionTypeSelectedItem.nextActionTypeID) || null
                                : null}
                              disabled={!formState.nextActionTypeSelect || nextActionTypeOptions.length <= 1 || areDropdownsLocked || disabledNextAction || formFieldAccess?.nextActionTypeSelect?.hide}
                              onChange={(event, newValue) => {
                                const modifiedEvent = {
                                  target: {
                                    name: "nextActionTypeSelect",
                                  },
                                };
                                handleNextActionTypeChange(modifiedEvent, newValue);
                              }}
                              renderInput={(params) => (
                                <TextField {...params}
                                  label="Next Action Type"
                                  variant="outlined"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                          <label>
                            <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.nextActionTypeSelect}</span>
                          </label>
                        </div>                        
                      </div>
                      <div className='form-group'>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FormControl fullWidth>
                            <DatePicker
                              selected={
                                formValues.nextActionDueDateTxt
                                  ? parseDate(formValues.nextActionDueDateTxt): null
                              }
                              onChange={handleNextActionDueDateChange}
                              excludeDates={holidayList}
                              minDate={new Date(
                                new Date().getFullYear(),
                                new Date().getMonth(),
                                new Date().getDate()
                              )}
                              maxDate={getNYDateToday()}
                              disabled={!formState.nextActionDueDateTxt || areDropdownsLocked || !isUserAllowedNextActionDue() || disabledNextAction || formFieldAccess?.nextActionDueDate?.hide}
                              filterDate={(date) => {
                                const day = date.getDay();
                                return day !== 0 && day !== 6;
                              }}
                               placeholderText="Select date"
                              customInput={
                                <TextField
                                  label="Next Action Due"
                                  variant="outlined"
                                  fullWidth
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  sx={{
                                    '& .MuiInputBase-input': {
                                      color: formState.nextActionDueDateTxt ? '#000' : '#666',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                      color: '#666',
                                      opacity: 1,
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      '&.Mui-focused': {
                                        '& .MuiIconButton-root .MuiSvgIcon-root': {
                                          color: '#5A73EA !important',
                                        }
                                      }
                                    }
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton disabled={!formState.nextActionDueDateTxt || areDropdownsLocked || !isUserAllowedNextActionDue()} onClick={handleNextActionDueDateChangeClear}>
                                          <ClearIcon sx={{ color: '#666' }} />
                                        </IconButton>
                                      </InputAdornment>
                                    )
                                  }}
                                />
                              }
                            />
                          </FormControl>
                        </div>
                      </div>
                    </div>


                    <div className='form-row'>
                      <div className='form-group'>
                        <div className="d-flex justify-content-between align-items-center mb-2 form-actions-2">
                          <div className='rightDiv'>
                            {!(formFieldAccess.addToListBtn?.hide ?? false) && (
                              <Button id="addToListBtn" name="addToListBtn" variant='contained' color='ywButtonBlue' className='me-3' onClick={handleAddToListClick} 
                              disabled={resultsListOfActions.length > 0 || 
                                (
                                  (!formValues.caseNumberTxt && !formValues.noCaseChk) ||
                                  !(formValues.workflowSelectedRow?.workflowID > 0) ||
                                  !(formValues.nextActionTypeSelectedItem?.nextActionTypeID > 0) ||
                                  !(formValues.actionTypeSelectedItem?.actionTypeID > 0) ||
                                  !formValues.nextActionDueDateTxt
                                )}>
                                  ADD TO LIST</Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='form-row'>
                      <div className='form-group'>
                        {!(formFieldAccess.noteForActionTxt?.hide ?? false) && (
                          <div className="col-12 mb-3 align-items-center justify-center">
                            <TextAreaWithCount
                              ref={noteForActionRef}
                              id="noteForActionTxt"
                              nameForField="noteForActionTxt"
                              labelValue="Y-Trac Note for Action Taken"
                              maxLength={notesCharLimit}
                              placeholder=""
                              isDisabled={!formState.noteForActionTxt}
                              onTextChange={handleNotesChange}
                              requiredFieldLabel={requiredFieldLabels.noteForActionTxt}
                            />
                          </div>
                        )}

                      </div>
                    </div>

                    <div className='form-row'>
                      <div className='form-group'>
                        {!(formFieldAccess.narrativeForStateTxt?.hide ?? false) && (
                          <div className="col-12 mb-3 align-items-center justify-center">
                            <TextAreaWithCount
                              ref={narrativeForStateRef}
                              id="narrativeForStateTxt"
                              nameForField="narrativeForStateTxt"
                              labelValue="Narrative for State System"
                              maxLength={narrativeCharLimit}
                              placeholder=""
                              isDisabled={!formState.narrativeForStateTxt || (areDropdownsLocked && formValues.noCaseChk)}
                              onTextChange={handleNarrativeChange}
                              requiredFieldLabel={requiredFieldLabels.narrativeForStateTxt}
                            />
                          </div>
                        )}
                      </div>
                    </div>


                  </form>

                  {/* DataGrid */}
                  {!(formFieldAccess.listOfActionsDgrd?.hide ?? false) && (
                    <div className="data-grid-container">
                      {/* DataGrid for List of Actions */}
                      <div className="yt-button-container">
                        <div className="text-wrapper-header-bold-2" style={{ marginTop: '5px' }}>List of Actions</div>


                      </div>
                      <div style={{ marginTop: '2px', height: '100%', width: "100%" }}>
                        {hasListOfActions ?
                          <DataGridPremium
                            id="listOfActionsDgrd"
                            name="listOfActionsDgrd"
                            rows={resultsListOfActions}
                            columns={listOfActionsGridColumns}
                            rowsPerPageOptions={[5, 10, 15, 20, 25]}
                            pagination
                            pageSizeOptions={[5, 10, 15, 20, 25]}
                            paginationModel={paginationModel} // Controlled pagination
                            onPaginationModelChange={setPaginationModel} // Handle page changes                    
                            getRowId={(row) => `${row.rowIndex}`}
                            filterModel={gridFilterModel}
                            onFilterModelChange={handleGridFilterChange}
                            disableSelectionOnClick
                            sx={{
                              "& .MuiDataGrid-row:hover": {
                                cursor: "pointer"
                              },
                              ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
                                "margin-top": "1em",
                                "margin-bottom": "1em"
                              },
                              "& .MuiDataGrid-virtualScroller": { overflowX: "auto", overflowY: "auto" }
                            }}
                          />
                          : initialSearch && (<div className="text-wrapper-subtitle">No action(s) found!</div>)
                        }
                      </div>
                    </div>
                  )}

                </div>
              </div>

              {/* Custom Success Message */}
              {showSuccessMessage && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                  <div 
                    className="yt-dialog-success" 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '12px 16px',
                      borderRadius: '4px',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: '500',
                      textAlign: 'center',
                      cursor: 'pointer',
                      gap: '8px',
                      width: '75%'
                    }}
                  >
                    <CheckCircleIcon 
                      className="yt-dialog-icon" 
                      style={{ 
                        fontSize: '24px', 
                        color: '#fff',
                        flexShrink: 0
                      }} 
                    />
                    <span>{successMessageText}</span>
                  </div>
                </div>
              )}

              {/* Footer Section */}
              <footer className="form-footer">
                <div className="d-flex justify-content-between align-items-center mb-2 form-actions">
                  {!(formFieldAccess.cancelBtn?.hide ?? false) && (
                    <div>
                      <Button id="cancelBtn" name="cancelBtn" color="ywButtonWhite" variant='outlined' onClick={handleCancelClick}>Cancel</Button>
                    </div>
                  )}
                  {!(formFieldAccess.leftPaneMessageLbl?.hide ?? false) && (
                    <div className="center-section text-wrapper-message">
                      <div><label id="leftPaneMessageLbl" name="leftPaneMessageLbl">{leftPaneMessage}</label></div>
                      {isTimerVisible &&
                        <div><label className='timer-message'>Timer: {timerView}</label></div>
                      }
                    </div>
                  )}
                  <div style={{ display: "flex", gap: "18px" }}>
                    {!(formFieldAccess.saveBtn?.hide ?? false) && (
                      <Button id="saveBtn" name="saveBtn" color="ywButtonBlack" variant='contained' className='me-2' disabled={resultsListOfActions.length === 0 || !isSaveEnabled || (formValues.noCaseChk && (!formValues.noteForActionTxt || formValues.noteForActionTxt.trim() === ""))} onClick={handleSaveClick}>Save</Button>
                    )}
                  </div>
                </div>
              </footer>
            </div>
          </div>
        </div>
      </div >
    </div>
  );

  const rightPaneContent = (
    <div style={{ width: '100%', height: '100%' }}>
      <CaseInformationPane
        ref={caseInfoRef}
        onCaseSearchComplete={handleCaseSearchComplete}
        onMessageUpdate={handleShowBatchDocMessage}
        pageName={'New Work Item'}
      />
    </div>
  );

  return (
    <div>
      <ToggleSplitPane
        leftPaneVisibility={true}
        leftPaneTitle={leftPaneTitle}
        leftPaneContent={leftPaneContent}
        rightPaneTitle={rightPaneTitle}
        rightPaneContent={rightPaneContent}
        leftPaneSize='50%'
      >
      </ToggleSplitPane>

      <AlertNotification
        alertType={alertProps.type}
        message={alertProps.message}
        open={alertProps.open}
        onClose={handleAlertClose}
      />

      {/* Include the loader component */}
      <PageLoader isPageLoading={isPageLoading} />

      {/* Include the Confirmation Dialog component */}
      <ConfirmationDialog
        openConfirmation={confirmDialogOpen}
        dialogContent={confirmationDialogContent}
        onCloseConfirmation={handleCloseConfirmDialog}
        onConfirmDialog={(param) => handleDialogConfirmAction(param)}
      />

      {/* Include the Notification Dialog component */}
      <NotificationDialog
        openNotification={notificationDialogOpen}
        dialogContent={notificationDialogContent}
        onCloseNotification={handleCloseNotificationDialog}
        isAutoClose={timerAutoCloseNotificationDialog}
        timerDuration={timerNotificationDialogClose}
        onConfirm = {handleOkClick}
      />

    </div>
  );
});

export default NewWorkitem;
